import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import CountrySelect from './CountrySelect';
import CurrencySelect from './CurrencySelect';
import { CheckIcon, UploadIcon } from './Icons';

export default function BankAccountForm({ 
  initialData, 
  currencies, 
  onSave, 
  onCancel, 
  saving = false 
}) {
  const [formData, setFormData] = useState(initialData);
  const [documentFile, setDocumentFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    if (formData.id && formData.has_document) {
      setPreviewLoading(true);
      axios.get(`/users/me/bank-accounts/${formData.id}/document`, { signal: controller.signal })
        .then(res => setPreviewUrl(res.data.url))
        .catch(err => {
          if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
          console.error("Failed to load document preview", err);
        })
        .finally(() => setPreviewLoading(false));
    }
    return () => controller.abort();
  }, [formData.id, formData.has_document]);
  
  const isIbanCountry = (code) => ['GB','FR','DE','IT','ES','NL','AE','SA','QA','IE','CH','AT','BE','PT'].includes(code?.toUpperCase());
  const isRoutingCountry = (code) => ['US','GB','AU','IN'].includes(code?.toUpperCase());
  const getRoutingLabel = (code) => {
    switch (code?.toUpperCase()) {
      case 'US': return 'Routing Number (ABA)';
      case 'GB': return 'Sort Code';
      case 'AU': return 'BSB Number';
      case 'IN': return 'IFSC Code';
      default: return 'Routing Code';
    }
  };

  const handleCountryChange = (c) => {
    let curr = formData.currency;
    const eurCountries = ['AT','BE','CY','EE','FI','FR','DE','GR','IE','IT','LV','LT','LU','MT','NL','PT','SK','SI','ES'];
    if (eurCountries.includes(c.iso2)) curr = 'EUR';
    else if (c.iso2 === 'US') curr = 'USD';
    else if (c.iso2 === 'GB') curr = 'GBP';
    else if (c.iso2 === 'AU') curr = 'AUD';
    else if (c.iso2 === 'IN') curr = 'INR';
    else if (c.iso2 === 'SG') curr = 'SGD';
    else if (c.iso2 === 'AE') curr = 'AED';
    else if (c.iso2 === 'CA') curr = 'CAD';
    else if (c.iso2 === 'LK') curr = 'LKR';
    
    setFormData(prev => ({
      ...prev,
      country_code: c.iso2,
      currency: curr,
      iban: '',
      routing_code: '',
      account_number: ''
    }));
  };

  const handleSave = () => {
    onSave(formData, documentFile);
  };

  return (
    <div className="bg-white dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm mt-3">
      <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-4">
        {formData.id ? 'Edit Bank Account' : 'Add Bank Account'}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Country</label>
          <CountrySelect value={formData.country_code} onChange={handleCountryChange} preload={true} />
        </div>
        
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Bank Name</label>
          <input type="text" value={formData.bank_name || ''} onChange={e => setFormData({...formData, bank_name: e.target.value})} placeholder="e.g. Chase, HSBC" className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500" />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Account Holder Name</label>
          <input type="text" value={formData.account_holder_name || ''} onChange={e => setFormData({...formData, account_holder_name: e.target.value})} placeholder="Name as appears on account" className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500" />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Currency</label>
          <CurrencySelect
            value={formData.currency}
            currencies={currencies}
            onChange={c => setFormData({...formData, currency: c.code})}
          />
        </div>

        {isIbanCountry(formData.country_code) ? (
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">IBAN</label>
            <input type="text" value={formData.iban || ''} onChange={e => setFormData({...formData, iban: e.target.value.toUpperCase()})} placeholder="GB00..." className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 font-mono" />
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Account Number</label>
            <input type="text" value={formData.account_number || ''} onChange={e => setFormData({...formData, account_number: e.target.value})} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 font-mono" />
          </div>
        )}

        {isRoutingCountry(formData.country_code) && (
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">{getRoutingLabel(formData.country_code)}</label>
            <input type="text" value={formData.routing_code || ''} onChange={e => setFormData({...formData, routing_code: e.target.value})} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 font-mono" />
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">SWIFT / BIC (Optional)</label>
          <input type="text" value={formData.swift_bic || ''} onChange={e => setFormData({...formData, swift_bic: e.target.value.toUpperCase()})} placeholder="8 or 11 characters" className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 font-mono" />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Branch Name (Optional)</label>
          <input type="text" value={formData.branch_name || ''} onChange={e => setFormData({...formData, branch_name: e.target.value})} className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500" />
        </div>

        
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Verification Document {formData.id && '(Upload new to replace)'}</label>
          {formData.id && formData.has_document && !documentFile && (
            <div className="mb-3 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-800 dark:text-slate-200">Current Document</p>
                {previewLoading ? (
                  <p className="text-xs text-slate-500">Loading preview...</p>
                ) : previewUrl ? (
                  <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline">
                    View Document (Opens in new tab)
                  </a>
                ) : (
                  <p className="text-xs text-red-500">Preview unavailable</p>
                )}
              </div>
              {previewUrl && (
                <div className="h-16 w-16 bg-white border border-slate-200 rounded overflow-hidden flex items-center justify-center">
                  <object data={previewUrl} className="w-full h-full object-cover">
                    <span className="text-[10px] text-slate-400">Preview</span>
                  </object>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-center w-full">
            <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-indigo-500">
              <div className="flex flex-col items-center justify-center pt-3 pb-3 text-slate-500">
                {documentFile ? (
                  <>
                    <CheckIcon className="w-7 h-7 text-emerald-500 mb-1" />
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">{documentFile.name}</p>
                    <p className="text-xs text-slate-400">{(documentFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </>
                ) : (
                  <>
                    <UploadIcon className="w-7 h-7 mb-1.5 text-slate-400" />
                    <p className="mb-1 text-xs font-semibold text-slate-700 dark:text-slate-300">Click to upload or drag and drop</p>
                    <p className="text-[11px] text-slate-400">PDF, PNG, JPG (Max 5MB - Original Resolution)</p>
                  </>
                )}
              </div>
              <input type="file" className="hidden" accept=".pdf,image/png,image/jpeg,image/jpg" onChange={(e) => setDocumentFile(e.target.files[0])} />
            </label>
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2.5">
        <button type="button" onClick={onCancel} className="px-3.5 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition">Cancel</button>
        <button type="button" onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-3.5 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 transition shadow-sm">
          {saving ? 'Saving...' : 'Save Bank Account'}
        </button>
      </div>
    </div>
  );
}
